class BundleVideoHandler {
  constructor() {
    this.videos = document.querySelectorAll(".bundle-video");
    this.observerOptions = {
      root: null,
      rootMargin: "50px",
      threshold: 0.5,
    };

    this.videoStates = new Map();
    this.swiperInstances = new Map();
    this.init();
  }

  init() {
    this.setupIntersectionObserver();
    this.setupSwiperCarousels();
    this.setupVideoHandlers();
  }

  setupIntersectionObserver() {
    this.observer = new IntersectionObserver(this.handleIntersection.bind(this), this.observerOptions);

    this.videos.forEach((video) => {
      this.videoStates.set(video, {
        hasPlayedOnce: false,
        isEnded: false,
        isPlaying: false,
        isInCarousel: video.closest(".swiper") !== null,
      });

      this.observer.observe(video);

      video.muted = true;
      video.playsInline = true;
    });
  }

  setupVideoHandlers() {
    this.videos.forEach((video) => {
      video.addEventListener("ended", () => {
        const state = this.videoStates.get(video);
        if (state) {
          state.isEnded = true;
          state.isPlaying = false;
        }
      });

      video.addEventListener("play", () => {
        const state = this.videoStates.get(video);
        if (state) {
          state.isPlaying = true;
        }
      });

      video.addEventListener("pause", () => {
        const state = this.videoStates.get(video);
        if (state) {
          state.isPlaying = false;
        }
      });
    });
  }

  setupSwiperCarousels() {
    const carousels = document.querySelectorAll(".bundle-media");

    carousels.forEach((carousel) => {
      const swiper = new Swiper(carousel, {
        loop: true,
        navigation: {
          nextEl: carousel.querySelector("[data-btn-next]"),
          prevEl: carousel.querySelector("[data-btn-prev]"),
        },
        pagination: {
          el: carousel.querySelector("[data-pagination]"),
          clickable: true,
        },
        on: {
          slideChangeTransitionEnd: (swiper) => {
            this.handleSlideChangeEnd(carousel, swiper);
          },
        },
      });

      this.swiperInstances.set(carousel, swiper);
    });
  }

  handleSlideChange(carousel, swiper) {
    const videos = carousel.querySelectorAll(".bundle-video");
    videos.forEach((video) => {
      this.pauseVideo(video);
    });
  }

  handleSlideChangeEnd(carousel, swiper) {
    const videos = carousel.querySelectorAll(".bundle-video");
    videos.forEach((video) => {
      this.resetVideo(video);
    });

    const activeSlide = carousel.querySelector(".swiper-slide-active");
    if (!activeSlide) {
      return;
    }
    const activeVideo = activeSlide.querySelector(".bundle-video");
    if (!activeVideo) {
      return;
    }
    const carouselRect = carousel.getBoundingClientRect();
    const isCarouselVisible = carouselRect.top < window.innerHeight && carouselRect.bottom > 0;
    if (!isCarouselVisible) {
      return;
    }

    this.playVideo(activeVideo);
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      const video = entry.target;
      const state = this.videoStates.get(video);

      if (!state) {
        return;
      }

      if (!entry.isIntersecting) {
        if (state.isPlaying) {
          this.pauseVideo(video);
        }

        return;
      }

      if (!state.isInCarousel) {
        if (!state.hasPlayedOnce) {
          this.playVideo(video);
          state.hasPlayedOnce = true;
        }

        return;
      }

      const swiper = video.closest(".swiper");
      if (!swiper) {
        return;
      }

      const activeSlide = swiper.querySelector(".swiper-slide-active");
      if (!activeSlide) {
        return;
      }

      const isActiveSlide = activeSlide.contains(video);
      if (isActiveSlide && !state.hasPlayedOnce) {
        this.playVideo(video);
        state.hasPlayedOnce = true;
      }
    });
  }

  playVideo(video) {
    const state = this.videoStates.get(video);
    if (!state || state.isPlaying) {
      return;
    }

    if (video.readyState >= 3) {
      video.play().catch((error) => {
        console.warn("Video autoplay prevented:", error);
      });
    } else {
      video.addEventListener(
        "canplay",
        () => {
          video.play().catch((error) => {
            console.warn("Video autoplay prevented:", error);
          });
        },
        { once: true },
      );
    }
  }

  pauseVideo(video) {
    const state = this.videoStates.get(video);
    if (!state || !state.isPlaying) {
      return;
    }

    video.pause();
    this.resetVideo(video);
  }

  resetVideo(video) {
    video.currentTime = 0;
    const state = this.videoStates.get(video);
    if (!state) {
      return;
    }

    state.isEnded = false;
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => new BundleVideoHandler());
} else {
  new BundleVideoHandler();
}
